import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/Button";
import { Building2, MapPin, Trash2 } from "lucide-react";
import ToggleSetting from "../../components/ui/ToggleSetting";
import { APIAddressSelect } from "../../components/ui/APIAddressSelect";
import FormField from "../../components/ui/FormField";
import SectionHeader from "../../components/ui/SectionHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import { updateSettings, updatePosDefaultScreen, updatePosDualScreen } from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";

import { useKeyboard } from "../../context/KeyboardContext";
import { useAuth } from "../../auth/AuthContext";
import { hasPackageModuleAccess } from "../../lib/packageModules";

const GeneralSettings = () => {
  const toast = useToast();
  const { settingsData, loadingSettings, refreshSettings } = useOutletContext<SettingsOutletContextType>();
  const { refreshConfig: refreshKeyboardConfig } = useKeyboard();
  const { user, updateUser, syncSettingsToLocalStorage } = useAuth();
  const hasPosModule = hasPackageModuleAccess(user, "POS");

  const [formData, setFormData] = useState({
    businessName: "",
    businessEmail: "",
    businessPhone: "",
    businessPhone2: "",
    businessLandline: "",
    dateFormat: "",
    timeFormat: "",
    businessWebsite: "",
    timezone: "",
    receiptFormat: "",
    currencyId: "",
    language: "",
    onScreenKeyboardVisibility: "Turn Off",
    onScreenKeyboardPositioning: "Top Center",
    contactDetailsVisibility: "Show",
    marketplaceEnable: true,
    address: "",
    address_1: "",
    city: "",
    postcode: "",
    country: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [posDefaultScreen, setPosDefaultScreen] = useState(false);
  const [posDualScreen, setPosDualScreen] = useState(false);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (settingsData && settingsData.settings) {
      const s = settingsData.settings;
      // Handle vkb_is_visible: backend stores as boolean (true/false)
      const keyboardVisibility = (s.vkb_is_visible === true || s.vkb_is_visible === 1 || Number(s.vkb_is_visible) === 1) ? "Turn On" : "Turn Off";

      const rawShowContact: any = s.show_contact_details;
      const showContact =
        rawShowContact === true ||
        rawShowContact === 1 ||
        rawShowContact === "1" ||
        rawShowContact === "true";

      setFormData({
        businessName: s.company_name || "",
        businessEmail: s.company_email || "",
        businessPhone: s.company_phone || "",
        businessPhone2: s.company_phone_2 || "",
        businessLandline: s.landline || "",
        dateFormat: s.date_format || "",
        timeFormat: s.time_format || "",
        businessWebsite: s.website || "",
        timezone: s.timezone || "",
        receiptFormat: s.receipt_format || "",
        currencyId: s.currency_id?.toString() || "",
        language: s.locale || "en",
        onScreenKeyboardVisibility: keyboardVisibility,
        onScreenKeyboardPositioning: s.vkb_position || "Top Center",
        contactDetailsVisibility: showContact ? "Show" : "Hide",
        marketplaceEnable:
          s.marketplace_enable === false ||
          s.marketplace_enable === 0 ||
          s.marketplace_enable === "0" ||
          s.marketplace_enable === "false"
            ? false
            : true,
        address: s.address || "",
        address_1: s.address_1 ?? "",
        city: s.city ?? "",
        postcode: s.postcode ?? "",
        country: s.country ?? "",
      });

      const val = s.pos_default_screen;
      setPosDefaultScreen(val === true || val === 1 || val === "1" || val === "active");
      const dual = s.pos_dual_screen;
      setPosDualScreen(dual === true || dual === 1 || dual === "1" || dual === "active");
    }
  }, [settingsData]);

  const handleFieldChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.businessName.trim()) {
      newErrors.businessName = "Business name is required";
    }

    if (!formData.businessEmail.trim()) {
      newErrors.businessEmail = "Business email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail)) {
      newErrors.businessEmail = "Please enter a valid email address";
    }

    if (!formData.businessPhone.trim()) {
      newErrors.businessPhone = "Business phone is required";
    }

    if (!formData.currencyId) {
      newErrors.currencyId = "Currency is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!settingsData || !settingsData.settings) return;

    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      const selectedCurrency = settingsData.currencies.find(c => c.id.toString() === formData.currencyId);

      await updateSettings(settingsData.settings.id, {
        company_name: formData.businessName,
        company_email: formData.businessEmail,
        company_phone: formData.businessPhone,
        company_phone_2: formData.businessPhone2,
        landline: formData.businessLandline,
        address: formData.address,
        address_1: formData.address_1,
        city: formData.city,
        postcode: formData.postcode,
        country: formData.country,
        website: formData.businessWebsite,
        date_format: formData.dateFormat,
        time_format: formData.timeFormat,
        currency_id: Number(formData.currencyId),
        currency_code: selectedCurrency?.currency_code || "GBP",
        currency_symbol: selectedCurrency?.currency_symbol || "£",
        timezone: formData.timezone,
        locale: formData.language,
        receipt_format: formData.receiptFormat,
        vkb_is_visible: formData.onScreenKeyboardVisibility === "Turn On",
        vkb_position: formData.onScreenKeyboardPositioning,
        // Backend expects string "true"/"false"
        show_contact_details:
          formData.contactDetailsVisibility === "Show" ? "true" : "false",
        marketplace_enable: formData.marketplaceEnable ? "true" : "false",
        print_receipt: settingsData.settings.print_receipt,
        printer_name: settingsData.settings.printer_name,
        is_sunbed: Number(settingsData.settings.is_sunbed),
        is_sunbed_tmax: Number(settingsData.settings.is_sunbed_tmax),
      });

      if (hasPosModule) {
        await updatePosDefaultScreen({
          pos_default_screen: posDefaultScreen ? "active" : "inactive",
        });
        await updatePosDualScreen({
          pos_dual_screen: posDualScreen ? "active" : "inactive",
        });
        if (user?.company) {
          updateUser({
            company: {
              ...user.company,
              pos_default_screen: posDefaultScreen,
              pos_dual_screen: posDualScreen,
            },
          });
        }
      }

      toast.success("Settings updated successfully!");
      refreshSettings();
      await syncSettingsToLocalStorage();
      refreshKeyboardConfig();
    } catch (error: any) {
      console.error("handleSave ERROR:", error);
      console.error("handleSave Response:", error.response);
      console.error("handleSave Response Data:", error.response?.data);

      const status = error.response?.status;
      let userMessage = "Failed to update general settings.";

      if (status === 404) {
        userMessage = "Settings not found. Please refresh the page.";
      } else if (status === 403) {
        userMessage = "You don't have permission to update settings.";
      } else if (status === 422) {
        userMessage = "Invalid data provided. Please check all fields.";
      } else if (status === 500) {
        userMessage = "Server error occurred. Please try again later.";
      } else if (error.message === "Network Error") {
        userMessage = "Connection lost. Please check your internet and try again.";
      }

      toast.error(userMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loadingSettings || !settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="General Settings"
        description="Configure your business information and preferences"
        variant="card"
      />

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Building2 size={18} className="text-white" />
            Business Information
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1 ">
          <div className="space-y-5">
            {/* Business Details Section */}
            <div>
              <SectionHeader title="Business Details" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Business Name *"
                  name="businessName"
                  type="text"
                  value={formData.businessName}
                  onChange={handleFieldChange}
                  placeholder="Enter business name"
                  error={errors.businessName}
                />
                <FormField
                  label="Business Email *"
                  name="businessEmail"
                  type="email"
                  value={formData.businessEmail}
                  onChange={handleFieldChange}
                  placeholder="Enter business email"
                  error={errors.businessEmail}
                />
                <FormField
                  label="Business Phone *"
                  name="businessPhone"
                  type="tel"
                  value={formData.businessPhone}
                  onChange={handleFieldChange}
                  placeholder="Enter business phone"
                  error={errors.businessPhone}
                />
                <FormField
                  label="Business Phone 2"
                  name="businessPhone2"
                  type="tel"
                  value={formData.businessPhone2}
                  onChange={handleFieldChange}
                  placeholder="Enter alternate phone"
                />
                <FormField
                  label="Business Landline"
                  name="businessLandline"
                  type="tel"
                  value={formData.businessLandline}
                  onChange={handleFieldChange}
                  placeholder="Enter landline"
                />
                <FormField
                  label="Currency *"
                  name="currencyId"
                  type="select"
                  value={formData.currencyId}
                  onChange={handleFieldChange}
                  error={errors.currencyId}
                  options={settingsData?.currencies?.map(c => ({
                    value: c.id.toString(),
                    label: `${c.currency_symbol} (${c.currency_code})`
                  })) || []}
                />
                <FormField
                  label="Business Website"
                  name="businessWebsite"
                  type="url"
                  value={formData.businessWebsite}
                  onChange={handleFieldChange}
                  placeholder="Enter website URL"
                  className="md:col-span-2"
                />
              </div>
            </div>

            {/* Regional Settings Section - Commented Out
            <div>
              <SectionHeader title="Regional Settings" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Date Format *"
                  name="dateFormat"
                  type="select"
                  value={formData.dateFormat}
                  onChange={handleFieldChange}
                  error={errors.dateFormat}
                  options={
                    settingsData?.date_formats
                    ? Object.entries(settingsData.date_formats).map(([format, label]) => ({
                        value: format,
                        label: label // or format + " " + label
                      }))
                    : []
                  }
                />
                <FormField
                  label="Time Format *"
                  name="timeFormat"
                  type="select"
                  value={formData.timeFormat}
                  onChange={handleFieldChange}
                  error={errors.timeFormat}
                  options={
                    settingsData?.time_formats
                    ? Object.entries(settingsData.time_formats).map(([format, label]) => ({
                        value: format,
                        label: label
                      }))
                    : []
                  }
                />
                <FormField
                  label="Timezone *"
                  name="timezone"
                  type="select"
                  value={formData.timezone}
                  onChange={handleFieldChange}
                  error={errors.timezone}
                  options={settingsData?.timezones?.map(tz => ({ value: tz, label: tz })) || []}
                />
                <FormField
                  label="Language *"
                  name="language"
                  type="select"
                  value={formData.language}
                  onChange={handleFieldChange}
                  error={errors.language}
                  options={settingsData?.enabled_languages?.map(l => ({
                    value: l.language_code,
                    label: l.language_name
                  })) || []}
                />
                <FormField
                  label="Receipt Format"
                  name="receiptFormat"
                  type="select"
                  value={formData.receiptFormat}
                  onChange={handleFieldChange}
                  options={
                    settingsData?.receipt_formats
                    ? Object.keys(settingsData.receipt_formats).map(rf => ({
                        value: rf,
                        label: rf
                      }))
                    : []
                  }
                />
              </div>
            </div>
            */}

            {/* Display Settings Section */}
            <div>
              <SectionHeader title="Display Settings" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="On-screen Keyboard Visibility"
                  name="onScreenKeyboardVisibility"
                  type="select"
                  value={formData.onScreenKeyboardVisibility}
                  onChange={handleFieldChange}
                  options={[
                    { value: "Turn Off", label: "Turn Off" },
                    { value: "Turn On", label: "Turn On" },
                  ]}
                />
                <FormField
                  label="On-screen Keyboard Positioning"
                  name="onScreenKeyboardPositioning"
                  type="select"
                  value={formData.onScreenKeyboardPositioning}
                  onChange={handleFieldChange}
                  options={[
                    { value: "Top Center", label: "Top Center" },
                    { value: "Bottom Center", label: "Bottom Center" },
                    { value: "Top Left", label: "Top Left" },
                    { value: "Top Right", label: "Top Right" },
                  ]}
                />
                <FormField
                  label="Contact Details Visibility"
                  name="contactDetailsVisibility"
                  type="select"
                  value={formData.contactDetailsVisibility}
                  onChange={handleFieldChange}
                  options={[
                    { value: "Show", label: "Show" },
                    { value: "Hide", label: "Hide" },
                  ]}
                />
              </div>
              <div className="mt-4 border border-gray-100 rounded-lg">
                <ToggleSetting
                  label="Marketplace"
                  description="Show this business on the Glossa marketplace. Turn off to hide listings and studio page."
                  checked={formData.marketplaceEnable}
                  onChange={(checked) =>
                    setFormData((prev) => ({ ...prev, marketplaceEnable: checked }))
                  }
                  showBackground
                />
              </div>
              {hasPosModule && (
                <>
                  <div className="mt-4 border border-gray-100 rounded-lg">
                    <ToggleSetting
                      label="POS as Default Screen"
                      description="Open the POS screen instead of the dashboard after login"
                      checked={posDefaultScreen}
                      onChange={setPosDefaultScreen}
                      showBackground
                    />
                  </div>
                  <div className="mt-4 border border-gray-100 rounded-lg">
                    <ToggleSetting
                      label="POS Customer Display (Dual Screen)"
                      description="Automatically open a second window for customers showing cart totals and payment status"
                      checked={posDualScreen}
                      onChange={setPosDualScreen}
                      showBackground
                    />
                  </div>
                </>
              )}
            </div>

            {/* Address Section */}
            <div>
              <SectionHeader title="Business Address" />
              <div className="space-y-1">
                <div className="flex justify-between">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  {errors.address && <span className="text-xs text-red-500">{errors.address}</span>}
                </div>
                <APIAddressSelect
                  value={formData.address}
                  onChange={(val) => handleFieldChange("address", val)}
                  onSelectDetails={(details) => {
                    if (details.address_1 !== undefined) handleFieldChange("address_1", details.address_1);
                    if (details.city !== undefined) handleFieldChange("city", details.city);
                    if (details.postcode !== undefined) handleFieldChange("postcode", details.postcode);
                    if (details.country !== undefined) handleFieldChange("country", details.country);
                  }}
                  placeholder="Enter business address"
                  error={errors.address}
                  icon={<MapPin size={17} />}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <FormField
                    label="Address line 1"
                    name="address_1"
                    type="text"
                    value={formData.address_1}
                    onChange={handleFieldChange}
                    placeholder="Enter address line 1"
                  />
                  <FormField
                    label="City"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleFieldChange}
                    placeholder="Enter city"
                  />
                  <FormField
                    label="Postcode"
                    name="postcode"
                    type="text"
                    value={formData.postcode}
                    onChange={handleFieldChange}
                    placeholder="Enter postcode"
                  />
                  <FormField
                    label="Country"
                    name="country"
                    type="text"
                    value={formData.country}
                    onChange={handleFieldChange}
                    placeholder="Enter country"
                  />
                </div>
              </div>
            </div>

            {/* Danger zone */}
            <div className="pt-6 mt-6 border-t border-gray-200">
              <SectionHeader title="Danger Zone" />
              <p className="text-sm text-gray-500 mb-3">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <Button
                type="button"
                onClick={() => setDeleteAccountModalOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2"
              >
                <Trash2 size={16} />
                Delete account
              </Button>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteAccountModalOpen}
        onClose={() => setDeleteAccountModalOpen(false)}
        onConfirm={() => {
          setDeleteAccountModalOpen(false);
          toast.success("Account deleted successfully.");
        }}
        title="Delete account"
        message="Are you sure you want to delete your account? This action cannot be undone."
        confirmText="Delete account"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default GeneralSettings;
